/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumHeroAlphaVariants, HeroAlphaFragment } from "@cosmos/hygraph-schema";

import { Animate, ResponsiveContainer, SectionContainer } from "@components/elements";
import { styled } from "@panda/jsx";
import { vstack } from "@panda/patterns";

import { HeroAlphaBlurredImage } from "./HeroAlpha.BlurredImage";
import { HeroAlphaContent } from "./HeroAlpha.Content";
import { HeroAlphaFullWidthImage } from "./HeroAlpha.FullWidthImage";
import { css } from "@panda/css";

const Styled = styled("main", {
	base: {
		display: "block",
		width: "100%",
	},
});

export const HeroAlpha = (props: HeroAlphaFragment | null): React.JSX.Element => {
	const isFullWidth = props?.variant === CosmosEnumHeroAlphaVariants.Fullwidth;
	const isBlurred = !isFullWidth; //default

	const content = (
		<HeroAlphaContent
			isBlurred={isBlurred}
			heroAsset={props?.heroAsset}
			logoImage={props?.logoImage}
			eyebrow={props?.eyebrow}
			title={props?.title}
			desc={props?.desc}
			buttons={props?.buttons || []}
			modButtons={props?.modButtons || []}
			gradient={props?.gradient}
		/>
	);

	const blurredLayout = (
		<ResponsiveContainer>
			<SectionContainer contentVisibility="unset" h="full">
				<section
					className={css({
						position: "relative",
						width: "1440px",
						h: "full",
						maxWidth: "full",
						maxHeight: "550px",
						lgDown: {
							maxHeight: "full",
						},
					})}
				>
					<Animate delay={0.35} type="fade">
						<HeroAlphaBlurredImage image={props?.blurredImage} width={977} height={550} />
					</Animate>
					{content}
				</section>
			</SectionContainer>
		</ResponsiveContainer>
	);

	const fullWidthLayout = (
		<>
			<HeroAlphaFullWidthImage heroAsset={props?.heroAsset} />
			<ResponsiveContainer>
				<SectionContainer overflowX="clip" contentVisibility="unset" h="100vh">
					{content}
				</SectionContainer>
			</ResponsiveContainer>
		</>
	);

	return (
		<Styled className={`hero-alpha ${vstack({ gap: "12" })}`}>{isBlurred ? blurredLayout : fullWidthLayout}</Styled>
	);
};
